import type { ChatMessage } from '../types/chat'
import {
  getDefaultVoiceNavApiBaseFromCatalog,
  isMedicalLinkHostname,
  isVoiceNavHostname,
} from '../api/apiConfig'
import {
  previewTokenFromSportsNocPreviewUrl,
  resolveSportsNocCertificatePreviewUrlFromMetadata,
} from './sportsNocPreviewUrl'
import {
  normalizeRenewOrdonnanceMeta,
  renewOrdonnanceAssistantBubbleText,
  resolveRenewOrdonnancePreviewUrlFromMetadata,
} from './renewOrdonnanceFlow'
import { chatContentString, chatContentTrim } from './chatContent'
import {
  folderOptionsDisplayContent,
  resolveFolderOptionsFromAssistantRow,
} from './folderOptionsFromAssistant'
import {
  dedupePatientSwitchYesUserMessages,
  enrichPatientSwitchConfirmOnMessage,
  parsePatientSwitchConfirmFromMetadata,
} from './patientSwitchConfirm'
import { parseReadModalityConfirmFromMetadata } from './readModalityConfirm'
import { parseCheckExamReceiptConfirmFromMetadata } from './checkExamReceiptConfirm'
import {
  normalizeSportsNocLocationOptionsFromApi,
  normalizeSportsNocSportOptionsFromApi,
  sportsNocFieldsFromAssistantMetadata,
} from './sportsNocFlow'
import {
  isPostRefreshUiDebugChatMessageId,
  withoutPostRefreshUiDebugMessages,
} from './sessionPendingCommandsDebug'
import { wireNotePreviewPayload } from './noteTaskWorkflow'
import { remindPreviewToWireMeta, wireRemindPreviewPayload } from './reminderPreviewInlineEdit'

/** Wire shape for GET/POST `/session/chat-history` (matches backend). */
export type SessionChatWireMessage = {
  id: string
  role: 'user' | 'assistant' | 'system'
  text: string
  created_at_utc?: string | null
  metadata?: Record<string, unknown>
}

const EPHEMERAL_STRIPS: (keyof ChatMessage)[] = [
  'autoOpenModalityFile',
  'autoOpenPrescriptionDocument',
  'openNavigationUrl',
  'closeModalityPreview',
  'patientOptionsRefresh',
  'sportsNocFlowTrigger',
  'sportsNocFlowCancelled',
  'cancelNotice',
]

function stripEphemeralForHistory(m: ChatMessage): ChatMessage {
  const out = { ...m }
  for (const k of EPHEMERAL_STRIPS) {
    delete out[k]
  }
  delete out.historySnapshot
  delete out.omitFromServerChatHistory
  return out
}

/** True for messages restored from GET /session/chat-history (read-only transcript). */
export function isHistorySnapshot(m: ChatMessage): boolean {
  return m.historySnapshot === true
}

/**
 * Redis-backed history is display-only: strip navigation URLs, pending confirmations, and prompts
 * so refresh cannot re-trigger mail/remind/patient-picker flows from stale state.
 */
export function sanitizeHistorySnapshotMessage(m: ChatMessage): ChatMessage {
  const out: ChatMessage = { ...m, historySnapshot: true }

  delete out.openPatientDossierUrl
  delete out.mockPatientPage
  delete out.openNavigationUrl
  delete out.autoOpenModalityFile
  delete out.autoOpenPrescriptionDocument
  delete out.prescriptionDocumentPreviewUrl
  delete out.prioritizeDocumentAutoOpen
  delete out.sportsNocCertificatePreview
  delete out.renewOrdonnanceDocumentPreview
  delete out.closeModalityPreview
  delete out.sportsNocFlowTrigger
  delete out.sportsNocFlowCancelled
  delete out.patientOptionsRefresh

  delete out.awaitingMessageConfirmation
  delete out.awaitingMessageEditField
  delete out.awaitingMailConfirmation
  delete out.awaitingGeneralConfirmation
  delete out.generalConfirmationContext
  delete out.patientSwitchConfirm
  delete out.patientSwitchConfirmResolved
  delete out.readModalityYesNoPrompt
  delete out.readModalityYesNoResolved
  delete out.checkExamReceiptYesNoPrompt
  delete out.checkExamReceiptYesNoResolved
  delete out.awaitingRemindConfirmation
  delete out.awaitingRemindEditField
  delete out.remindFlowActive
  delete out.reminderPreviewUpdateInPlace
  delete out.secretaryPreviewUpdateInPlace
  out.secretaryPreviewSuperseded = true
  delete out.sportsNocYesNoPrompt
  delete out.sportsNocAwaitingField
  delete out.sportsNocNeedsClarification
  delete out.sportsNocSummary
  delete out.sportsNocGenerateConfirm
  delete out.sportsNocPreviewUpdateInPlace
  delete out.renewOrdonnanceMailPrompt
  delete out.renewOrdonnanceRenewing
  delete out.unreadMessagesUi
  // History transcript should read like plain chat: do not keep interactive option-card UIs.
  if (
    out.uiType === 'patient_options' ||
    out.uiType === 'folder_options' ||
    out.uiType === 'assignee_options' ||
    out.uiType === 'recipient_type_options' ||
    out.uiType === 'delivery_channel_options' ||
    out.uiType === 'prescription_type_options' ||
    out.uiType === 'sports_noc_location_options' ||
    out.uiType === 'sports_noc_sports_options'
  ) {
    delete out.uiType
    delete out.options
    delete out.patientOptionsPaging
    delete out.sportsNocLocationOptions
    delete out.sportsNocSportOptions
    delete out.sportsNocSportSelectedIds
  }

  if (out.uiType === 'patient_options') {
    out.patientOptionsResolved = true
  }
  if (out.uiType === 'folder_options') {
    out.folderOptionsResolved = true
  }
  if (out.uiType === 'assignee_options') {
    out.assigneeOptionsResolved = true
  }
  if (out.uiType === 'recipient_type_options') {
    out.recipientTypeOptionsResolved = true
  }
  if (out.uiType === 'delivery_channel_options') {
    out.deliveryChannelOptionsResolved = true
  }
  if (out.uiType === 'sports_noc_location_options') {
    out.sportsNocLocationOptionsResolved = true
  }
  if (out.uiType === 'sports_noc_sports_options') {
    out.sportsNocSportOptionsResolved = true
    delete out.sportsNocSportSelectedIds
  }

  if (out.mailDocument) {
    out.mailDocument = {
      ...out.mailDocument,
      awaitingMailConfirmation: false,
    }
  }

  if (out.secretaryMessage) {
    const sm = { ...out.secretaryMessage }
    if (sm.autofill_note === 'awaiting_send_confirmation') {
      delete sm.autofill_note
    }
    out.secretaryMessage = sm
  }

  if (out.pendingModalityExam?.status === 'pending') {
    out.pendingModalityExam = {
      ...out.pendingModalityExam,
      status: 'done',
      replayPending: true,
    }
  }

  if (out.modalityMeta?.filePath) {
    out.modalityMeta = { ...out.modalityMeta, filePath: undefined }
  }

  if (out.prescriptionMeta?.ordonnance_file_url) {
    out.prescriptionMeta = { ...out.prescriptionMeta, ordonnance_file_url: undefined }
  }

  if (m.reminderPreview && !out.reminderConfirmed && !out.reminderCancelled) {
    out.reminderPreviewResolved = true
    out.reminderYesNoSuperseded = true
  }

  if (out.sportsNocYesNoPrompt && !out.sportsNocYesNoResolved) {
    out.sportsNocYesNoResolved = true
  }

  if (out.renewOrdonnanceMailPrompt && !out.renewOrdonnanceMailResolved) {
    out.renewOrdonnanceMailResolved = true
  }
  if (out.renewOrdonnanceYesNoPrompt && !out.renewOrdonnanceMailResolved) {
    out.renewOrdonnanceMailResolved = true
  }

  if (
    /^(sports\s+no[-\s]*objection\s+certificate|certificat\s+de\s+non[-\s]*objection\s+pour\s+le\s+sport)\s*$/i.test(
      chatContentTrim(out.content),
    )
  ) {
    out.content = ''
  }

  return out
}

function buildSportsNocWireMetadata(m: ChatMessage): Record<string, unknown> | undefined {
  const hasSports =
    m.sportsNocAwaitingField != null ||
    m.sportsNocNeedsClarification === true ||
    m.sportsNocSummary != null ||
    m.sportsNocYesNoPrompt != null ||
    m.sportsNocGenerateConfirm === true ||
    m.sportsNocCertificateGenerated === true ||
    m.sportsNocFrench != null
  if (!hasSports) return undefined
  const sn: Record<string, unknown> = {
    domain: 'sports_noc',
    intent_action: 'generate_sports_noc_certificate',
  }
  if (m.sportsNocAwaitingField) sn.awaiting_field = m.sportsNocAwaitingField
  if (m.sportsNocNeedsClarification === true) sn.needs_clarification = true
  if (m.sportsNocFrench != null) sn.french = m.sportsNocFrench
  if (m.sportsNocSummary) sn.summary = m.sportsNocSummary
  if (m.sportsNocYesNoPrompt) sn.yes_no_prompt = m.sportsNocYesNoPrompt
  if (m.sportsNocGenerateConfirm === true) sn.generate_confirm = true
  if (m.sportsNocCertificateGenerated === true) sn.certificate_generated = true
  const previewUrl = (m.prescriptionDocumentPreviewUrl || m.modalityMeta?.filePath || '').trim()
  if (previewUrl) {
    sn.certificate_preview_url = previewUrl
    sn.certificate_pdf_url = previewUrl
    sn.file_path = previewUrl
    const token = previewTokenFromSportsNocPreviewUrl(previewUrl)
    if (token) sn.certificate_preview_token = token
  }
  if (m.mailDocument?.awaitingMailConfirmation && m.sportsNocYesNoPrompt?.kind === 'mail_certificate') {
    sn.mail_preview = {
      documentType: m.mailDocument.documentType,
      mailSubject: m.mailDocument.mailSubject,
      mailBody: m.mailDocument.mailBody,
      recipientName: m.mailDocument.recipientName,
      recipientType: m.mailDocument.recipientType,
    }
  }
  if (m.activePatientIdHint?.trim()) sn.resolved_patient_id = m.activePatientIdHint.trim()
  if (m.activePatientNameHint?.trim()) sn.resolved_patient_name = m.activePatientNameHint.trim()
  if (m.sportsNocLocationOptions?.length) {
    sn.location_options = m.sportsNocLocationOptions.map((o) => ({
      location_id: o.locationId,
      location_name: o.label,
    }))
  }
  if (m.sportsNocSportOptions?.length) {
    sn.sports_field_options = m.sportsNocSportOptions.map((o) => ({
      field_id: o.fieldId,
      field_name: o.label,
    }))
  }
  return sn
}

function metadataFromMessage(m: ChatMessage): Record<string, unknown> | undefined {
  const meta: Record<string, unknown> = {}
  const sportsWire = buildSportsNocWireMetadata(m)
  if (sportsWire) {
    meta.kind = 'sports_noc'
    meta.sports_noc = sportsWire
  }
  if (m.uiType) meta.uiType = m.uiType
  if (m.options?.length) meta.options = m.options
  if (m.sportsNocLocationOptions?.length) {
    meta.options = m.sportsNocLocationOptions.map((o) => ({
      location_id: o.locationId,
      label: o.label,
    }))
  }
  if (m.sportsNocLocationOptionsResolved != null) {
    meta.sportsNocLocationOptionsResolved = m.sportsNocLocationOptionsResolved
  }
  if (m.sportsNocSportOptions?.length) {
    meta.options = m.sportsNocSportOptions.map((o) => ({
      field_id: o.fieldId,
      label: o.label,
    }))
  }
  if (m.sportsNocSportOptionsResolved != null) {
    meta.sportsNocSportOptionsResolved = m.sportsNocSportOptionsResolved
  }
  if (m.sportsNocSportSelectedIds?.length) {
    meta.sportsNocSportSelectedIds = m.sportsNocSportSelectedIds
  }
  if (m.patientOptionsPaging) meta.patientOptionsPaging = m.patientOptionsPaging
  if (m.patientOptionsResolved != null) meta.patientOptionsResolved = m.patientOptionsResolved
  if (m.folderOptionsResolved != null) meta.folderOptionsResolved = m.folderOptionsResolved
  if (m.assigneeOptionsResolved != null) meta.assigneeOptionsResolved = m.assigneeOptionsResolved
  if (m.modalityMeta && Object.keys(m.modalityMeta).length) meta.modalityMeta = m.modalityMeta
  if (m.pendingModalityExam) {
    const p = m.pendingModalityExam
    meta.kind = 'pending_modality_exam'
    meta.modality = p.modality
    meta.recency = p.recency
    if (p.language) meta.language = p.language
    if (p.targetPatientName) meta.target_patient_name = p.targetPatientName
    if (p.targetPatientId) meta.target_patient_id = p.targetPatientId
    if (p.targetPatientUniqueId) meta.target_patient_unique_id = p.targetPatientUniqueId
    meta.status = p.status
    if (p.replayPending) meta.replay_pending = true
  }
  if (m.activePatientNameHint) {
    meta.activePatientNameHint = m.activePatientNameHint
    if (!sportsWire) {
      meta.patient = m.activePatientNameHint
      meta.kind = 'open_patient'
    }
  }
  if (m.activePatientIdHint) meta.activePatientIdHint = m.activePatientIdHint
  if (m.mockPatientPage) {
    meta.mock_patient_page = true
    meta.mock_patient_name = m.mockPatientPage.patientName
    if (m.mockPatientPage.patientDob) meta.mock_patient_dob = m.mockPatientPage.patientDob
    if (!meta.kind) meta.kind = 'open_patient'
    if (!meta.patient) meta.patient = m.mockPatientPage.patientName
  }
  if (m.noActivePatientContextHint != null) meta.noActivePatientContextHint = m.noActivePatientContextHint
  if (m.prescriptionMeta) meta.prescriptionMeta = m.prescriptionMeta
  if (m.bloodTestPrescriptionMeta) {
    meta.kind = 'blood_test_prescription'
    meta.bloodTestPrescriptionMeta = m.bloodTestPrescriptionMeta
    meta.blood_test_prescription = m.bloodTestPrescriptionMeta
  }
  if (m.renewOrdonnanceMeta) meta.renewOrdonnanceMeta = m.renewOrdonnanceMeta
  if (m.navigationMeta) meta.navigationMeta = m.navigationMeta
  if (m.secretaryMessage) meta.secretaryMessage = m.secretaryMessage
  if (m.mailDocument) meta.mailDocument = m.mailDocument
  if (m.reminderPreview) {
    meta.remind_preview = remindPreviewToWireMeta(m.reminderPreview)
    meta.reminderPreview = m.reminderPreview
    if (m.awaitingRemindConfirmation) {
      meta.kind = 'remind_me'
      meta.awaiting_remind_confirmation = true
      if (m.reminderPreview.datetimeUtc) meta.datetime_utc = m.reminderPreview.datetimeUtc
      if (m.reminderPreview.language) meta.language = m.reminderPreview.language
    }
  }
  if (m.notePreview) {
    meta.note_preview = m.notePreview
    if (m.awaitingNoteConfirmation) {
      meta.kind = 'create_note_task'
      meta.awaiting_note_confirmation = true
      if (m.notePreview.language) meta.language = m.notePreview.language
    }
  }
  if (m.awaitingNoteConfirmation != null) {
    meta.awaitingNoteConfirmation = m.awaitingNoteConfirmation
    if (m.awaitingNoteConfirmation) meta.awaiting_note_confirmation = true
  }
  if (m.noteConfirmed != null) meta.noteConfirmed = m.noteConfirmed
  if (m.noteCancelled != null) meta.noteCancelled = m.noteCancelled
  if (m.noteAwaitingContentClarification != null) {
    meta.noteAwaitingContentClarification = m.noteAwaitingContentClarification
    if (m.noteAwaitingContentClarification) {
      meta.awaiting_note_content_clarification = true
      meta.note_clarification = 'missing_content'
      if (!meta.kind) meta.kind = 'create_note_task'
    }
  }
  if (m.awaitingRemindConfirmation != null) {
    meta.awaitingRemindConfirmation = m.awaitingRemindConfirmation
    if (m.awaitingRemindConfirmation) meta.awaiting_remind_confirmation = true
  }
  if (m.reminderPreviewUpdateInPlace != null) {
    meta.reminderPreviewUpdateInPlace = m.reminderPreviewUpdateInPlace
    if (m.reminderPreviewUpdateInPlace) meta.reminder_preview_update_in_place = true
  }
  if (m.secretaryPreviewUpdateInPlace != null) {
    meta.secretaryPreviewUpdateInPlace = m.secretaryPreviewUpdateInPlace
    if (m.secretaryPreviewUpdateInPlace) meta.secretary_preview_update_in_place = true
  }
  if (m.awaitingMessageConfirmation != null) meta.awaitingMessageConfirmation = m.awaitingMessageConfirmation
  if (m.awaitingRecipientNameCorrection != null) {
    meta.awaitingRecipientNameCorrection = m.awaitingRecipientNameCorrection
    if (m.awaitingRecipientNameCorrection) meta.awaiting_recipient_name_correction = true
  }
  if (m.awaitingMailConfirmation != null) meta.awaitingMailConfirmation = m.awaitingMailConfirmation
  if (m.awaitingGeneralConfirmation != null) meta.awaitingGeneralConfirmation = m.awaitingGeneralConfirmation
  if (m.calendarTimeNeedsDateClarification) {
    meta.kind = 'calendar_time_retrieval'
    meta.needs_date_clarification = true
  }
  if (m.generalConfirmationContext?.trim()) {
    meta.generalConfirmationContext = m.generalConfirmationContext.trim()
    meta.general_confirmation_context = m.generalConfirmationContext.trim()
  }
  if (m.patientSwitchConfirm) meta.patientSwitchConfirm = m.patientSwitchConfirm
  if (m.patientSwitchConfirmResolved != null) {
    meta.patientSwitchConfirmResolved = m.patientSwitchConfirmResolved
  }
  if (m.readModalityYesNoPrompt) meta.readModalityYesNoPrompt = m.readModalityYesNoPrompt
  if (m.readModalityYesNoResolved != null) {
    meta.readModalityYesNoResolved = m.readModalityYesNoResolved
  }
  if (m.checkExamReceiptYesNoPrompt) meta.checkExamReceiptYesNoPrompt = m.checkExamReceiptYesNoPrompt
  if (m.checkExamReceiptYesNoResolved != null) {
    meta.checkExamReceiptYesNoResolved = m.checkExamReceiptYesNoResolved
  }
  if (m.reminderYesNoSuperseded != null) meta.reminderYesNoSuperseded = m.reminderYesNoSuperseded
  if (m.reminderPreviewResolved != null) meta.reminderPreviewResolved = m.reminderPreviewResolved
  if (m.reminderConfirmed != null) meta.reminderConfirmed = m.reminderConfirmed
  if (m.reminderCancelled != null) meta.reminderCancelled = m.reminderCancelled
  if (m.openPatientDossierUrl) meta.openPatientDossierUrl = m.openPatientDossierUrl
  if (m.sportsNocFrench != null) meta.sportsNocFrench = m.sportsNocFrench
  if (m.sportsNocAwaitingField) meta.sportsNocAwaitingField = m.sportsNocAwaitingField
  if (m.sportsNocNeedsClarification != null) meta.sportsNocNeedsClarification = m.sportsNocNeedsClarification
  if (m.sportsNocSummary) meta.sportsNocSummary = m.sportsNocSummary
  if (m.sportsNocYesNoPrompt) meta.sportsNocYesNoPrompt = m.sportsNocYesNoPrompt
  if (m.sportsNocYesNoResolved != null) meta.sportsNocYesNoResolved = m.sportsNocYesNoResolved
  if (m.renewOrdonnanceMailPrompt) meta.renewOrdonnanceMailPrompt = m.renewOrdonnanceMailPrompt
  if (m.renewOrdonnanceMailResolved != null) meta.renewOrdonnanceMailResolved = m.renewOrdonnanceMailResolved
  if (m.renewOrdonnanceRenewalCompleted) meta.renewOrdonnanceRenewalCompleted = true
  if (m.renewOrdonnanceFrench != null) meta.renewOrdonnanceFrench = m.renewOrdonnanceFrench
  if (m.renewOrdonnanceYesNoPrompt) meta.renewOrdonnanceYesNoPrompt = m.renewOrdonnanceYesNoPrompt
  if (Object.keys(meta).length === 0) return undefined
  try {
    const s = JSON.stringify(meta)
    if (s.length > 24000) return { truncated: true, uiType: m.uiType }
  } catch {
    return { truncated: true }
  }
  return meta
}

export function chatMessagesToWire(messages: ChatMessage[]): SessionChatWireMessage[] {
  return dedupePatientSwitchYesUserMessages(messages)
    .filter((raw) => !raw.omitFromServerChatHistory && !isPostRefreshUiDebugChatMessageId(raw.id))
    .map((raw) => {
    const m = stripEphemeralForHistory(raw)
    const row: SessionChatWireMessage = {
      id: m.id,
      role: m.role,
      text: m.content,
      created_at_utc: new Date(m.timestamp).toISOString(),
    }
    const md = metadataFromMessage(m)
    if (md) row.metadata = md
    return row
  })
}

export type WireMessagesToChatOptions = {
  /** When false, keep interactive sports Yes/No and prompts (live sync from server mid-session). */
  historySnapshot?: boolean
}

/** Hydrate Redis transcript for the live widget (interactive sports Yes/No, confirmations). */
export function wireRowsToLiveChatMessages(rows: SessionChatWireMessage[]): ChatMessage[] {
  return dedupePatientSwitchYesUserMessages(
    wireMessagesToChatMessages(rows, { historySnapshot: false }),
  )
}

/** Replace chat from server after /chat/command (drops post-refresh UI debug rows). */
export function mergeLiveChatPreservingSessionDebugRows(
  serverChat: readonly ChatMessage[],
  _prev: readonly ChatMessage[],
): ChatMessage[] {
  return withoutPostRefreshUiDebugMessages([...serverChat])
}

export function wireMessagesToChatMessages(
  rows: SessionChatWireMessage[],
  opts?: WireMessagesToChatOptions,
): ChatMessage[] {
  const asHistorySnapshot = opts?.historySnapshot !== false
  return rows.map((w) => {
    const role = w.role === 'system' ? 'assistant' : w.role
    const md = w.metadata
    const ts = w.created_at_utc ? Date.parse(w.created_at_utc) : Date.now()
    const base: ChatMessage = {
      id: w.id,
      role,
      content: chatContentString(w.text),
      timestamp: Number.isFinite(ts) ? ts : Date.now(),
    }
    if (!md || typeof md !== 'object') return base
    const out: ChatMessage = { ...base }
    if (md.uiType === 'patient_options') {
      out.uiType = 'patient_options'
      if (Array.isArray(md.options)) out.options = md.options as ChatMessage['options']
      if (md.patientOptionsPaging && typeof md.patientOptionsPaging === 'object')
        out.patientOptionsPaging = md.patientOptionsPaging as ChatMessage['patientOptionsPaging']
      if (typeof md.patientOptionsResolved === 'boolean') out.patientOptionsResolved = md.patientOptionsResolved
    }
    if (md.uiType === 'folder_options' || md.kind === 'folder_clarification') {
      const folderOpts = resolveFolderOptionsFromAssistantRow(
        md as Record<string, unknown>,
        md.options,
      )
      if (folderOpts?.length) {
        out.uiType = 'folder_options'
        out.options = folderOpts
        const frHint = /[àâäéèêëïîôùûçœ]|radiographie|documents cliniques/i.test(out.content)
        out.content = folderOptionsDisplayContent(out.content, folderOpts.length, frHint)
      }
      if (typeof md.folderOptionsResolved === 'boolean') out.folderOptionsResolved = md.folderOptionsResolved
    }
    if (md.uiType === 'assignee_options') {
      out.uiType = 'assignee_options'
      if (Array.isArray(md.options)) out.options = md.options as ChatMessage['options']
      if (typeof md.assigneeOptionsResolved === 'boolean') {
        out.assigneeOptionsResolved = md.assigneeOptionsResolved
      }
    }
    if (md.uiType === 'recipient_type_options') {
      out.uiType = 'recipient_type_options'
      if (Array.isArray(md.options)) out.options = md.options as ChatMessage['options']
      if (typeof md.recipientTypeOptionsResolved === 'boolean') {
        out.recipientTypeOptionsResolved = md.recipientTypeOptionsResolved
      }
    }
    if (md.uiType === 'delivery_channel_options') {
      out.uiType = 'delivery_channel_options'
      if (Array.isArray(md.options)) out.options = md.options as ChatMessage['options']
      if (typeof md.deliveryChannelOptionsResolved === 'boolean') {
        out.deliveryChannelOptionsResolved = md.deliveryChannelOptionsResolved
      }
      if (
        md.attachment_delivery_pending &&
        typeof md.attachment_delivery_pending === 'object'
      ) {
        out.attachmentDeliveryPending = md.attachment_delivery_pending as Record<string, unknown>
      }
    }
    if (md.uiType === 'prescription_type_options') {
      out.uiType = 'prescription_type_options'
      if (Array.isArray(md.options)) out.options = md.options as ChatMessage['options']
    }
    if (md.uiType === 'sports_noc_location_options') {
      out.uiType = 'sports_noc_location_options'
      const locOpts = normalizeSportsNocLocationOptionsFromApi(md.options ?? md.sportsNocLocationOptions)
      if (locOpts.length) out.sportsNocLocationOptions = locOpts
      if (typeof md.sportsNocLocationOptionsResolved === 'boolean') {
        out.sportsNocLocationOptionsResolved = md.sportsNocLocationOptionsResolved
      }
    }
    if (md.uiType === 'sports_noc_sports_options') {
      out.uiType = 'sports_noc_sports_options'
      const sportOpts = normalizeSportsNocSportOptionsFromApi(md.options ?? md.sportsNocSportOptions)
      if (sportOpts.length) out.sportsNocSportOptions = sportOpts
      if (typeof md.sportsNocSportOptionsResolved === 'boolean') {
        out.sportsNocSportOptionsResolved = md.sportsNocSportOptionsResolved
      }
      const selRaw = md.sportsNocSportSelectedIds
      if (Array.isArray(selRaw)) {
        out.sportsNocSportSelectedIds = selRaw
          .map((x) => Number(x))
          .filter((n) => Number.isFinite(n) && n > 0)
      }
    }
    if (md.modalityMeta && typeof md.modalityMeta === 'object') out.modalityMeta = md.modalityMeta as ChatMessage['modalityMeta']
    if (md.kind === 'pending_modality_exam') {
      const status = md.status === 'done' ? 'done' : 'pending'
      const replayPending =
        md.replay_pending === true || md.replayPending === true || status === 'pending'
      out.pendingModalityExam = {
        modality: typeof md.modality === 'string' ? md.modality : '',
        recency: typeof md.recency === 'string' ? md.recency : 'latest',
        language: typeof md.language === 'string' ? md.language : undefined,
        targetPatientName:
          typeof md.target_patient_name === 'string' ? md.target_patient_name : undefined,
        targetPatientId: typeof md.target_patient_id === 'string' ? md.target_patient_id : undefined,
        targetPatientUniqueId:
          typeof md.target_patient_unique_id === 'string' ? md.target_patient_unique_id : undefined,
        status,
        ...(replayPending ? { replayPending: true as const } : {}),
      }
    }
    if (md.mock_patient_page === true || md.mock_patient_page === 'true') {
      const mockName =
        typeof md.mock_patient_name === 'string' ? md.mock_patient_name.trim() : ''
      if (mockName) out.activePatientNameHint = mockName
    }
    if (typeof md.activePatientNameHint === 'string') out.activePatientNameHint = md.activePatientNameHint
    if (typeof md.activePatientIdHint === 'string') out.activePatientIdHint = md.activePatientIdHint
    if (md.kind === 'open_patient') {
      if (typeof md.patient === 'string' && md.patient.trim()) out.activePatientNameHint = md.patient.trim()
      const pid = md.patient_id != null ? String(md.patient_id).trim() : ''
      if (pid) out.activePatientIdHint = pid
    }
    if (typeof md.noActivePatientContextHint === 'boolean') out.noActivePatientContextHint = md.noActivePatientContextHint
    if (md.prescriptionMeta && typeof md.prescriptionMeta === 'object')
      out.prescriptionMeta = md.prescriptionMeta as ChatMessage['prescriptionMeta']
    const bloodWire =
      md.bloodTestPrescriptionMeta && typeof md.bloodTestPrescriptionMeta === 'object'
        ? md.bloodTestPrescriptionMeta
        : md.blood_test_prescription && typeof md.blood_test_prescription === 'object'
          ? md.blood_test_prescription
          : null
    if (bloodWire)
      out.bloodTestPrescriptionMeta = bloodWire as ChatMessage['bloodTestPrescriptionMeta']
    if (md.renewOrdonnanceMeta && typeof md.renewOrdonnanceMeta === 'object') {
      out.renewOrdonnanceMeta = normalizeRenewOrdonnanceMeta(
        md.renewOrdonnanceMeta as Record<string, unknown>,
      )
    } else if (
      md.kind === 'renew_ordonnance' &&
      md.renew_ordonnance &&
      typeof md.renew_ordonnance === 'object'
    ) {
      out.renewOrdonnanceMeta = normalizeRenewOrdonnanceMeta(
        md.renew_ordonnance as Record<string, unknown>,
      )
    }
    if (md.navigationMeta && typeof md.navigationMeta === 'object')
      out.navigationMeta = md.navigationMeta as ChatMessage['navigationMeta']
    if (md.secretaryMessage && typeof md.secretaryMessage === 'object')
      out.secretaryMessage = md.secretaryMessage as ChatMessage['secretaryMessage']
    else if (md.kind === 'secretary_message' && md.secretary_message && typeof md.secretary_message === 'object') {
      const sm = md.secretary_message as Record<string, unknown>
      out.secretaryMessage = sm as ChatMessage['secretaryMessage']
      if (sm.autofill_note === 'awaiting_send_confirmation') out.awaitingMessageConfirmation = true
    }
    if (md.mailDocument && typeof md.mailDocument === 'object') out.mailDocument = md.mailDocument as ChatMessage['mailDocument']
    else if (md.kind === 'mail_document' && md.mail_document && typeof md.mail_document === 'object') {
      const mdw = md.mail_document as Record<string, unknown>
      const docType =
        mdw.documentType != null && String(mdw.documentType).trim() !== ''
          ? String(mdw.documentType)
          : mdw.activeDocumentName != null
            ? String(mdw.activeDocumentName)
            : ''
      out.mailDocument = {
        currentIntent: 'mail_document',
        documentType: docType,
        mailSubject:
          mdw.mailSubject != null && String(mdw.mailSubject).trim() !== ''
            ? String(mdw.mailSubject)
            : docType,
        mailBody:
          mdw.mailBody != null && String(mdw.mailBody).trim() !== ''
            ? String(mdw.mailBody)
            : docType
              ? `${docType} enclosed.`
              : '',
        recipientType: mdw.recipientType != null ? String(mdw.recipientType) : '',
        recipientName: mdw.recipientName != null ? String(mdw.recipientName) : '',
        awaitingMailConfirmation: mdw.awaitingMailConfirmation === true,
        mail_sent_status: mdw.mail_sent_status != null ? String(mdw.mail_sent_status) : null,
      }
    }
    const remindPreviewRaw =
      (md.reminderPreview && typeof md.reminderPreview === 'object' ? md.reminderPreview : null) ||
      (md.remind_preview && typeof md.remind_preview === 'object' ? md.remind_preview : null)
    if (remindPreviewRaw) {
      out.reminderPreview = wireRemindPreviewPayload(remindPreviewRaw as Record<string, unknown>)
    }
    if (typeof md.awaitingMessageConfirmation === 'boolean') out.awaitingMessageConfirmation = md.awaitingMessageConfirmation
    else if (md.awaiting_message_confirmation === true) out.awaitingMessageConfirmation = true
    if (typeof md.awaitingRecipientNameCorrection === 'boolean') {
      out.awaitingRecipientNameCorrection = md.awaitingRecipientNameCorrection
    } else if (md.awaiting_recipient_name_correction === true || md.needs_recipient_clarification === true) {
      out.awaitingRecipientNameCorrection = true
    }
    if (typeof md.awaitingMailConfirmation === 'boolean') out.awaitingMailConfirmation = md.awaitingMailConfirmation
    else if (md.awaiting_mail_confirmation === true) out.awaitingMailConfirmation = true
    if (typeof md.awaitingGeneralConfirmation === 'boolean') out.awaitingGeneralConfirmation = md.awaitingGeneralConfirmation
    else if (md.awaiting_general_confirmation === true) out.awaitingGeneralConfirmation = true
    const gccRaw = md.generalConfirmationContext ?? md.general_confirmation_context
    if (typeof gccRaw === 'string' && gccRaw.trim()) {
      out.generalConfirmationContext = gccRaw.trim()
    }
    const pscParsed = parsePatientSwitchConfirmFromMetadata(md)
    if (pscParsed) out.patientSwitchConfirm = pscParsed
    if (!out.patientSwitchConfirm) {
      const inferred = enrichPatientSwitchConfirmOnMessage(out)
      if (inferred.patientSwitchConfirm) {
        out.patientSwitchConfirm = inferred.patientSwitchConfirm
        out.awaitingGeneralConfirmation = inferred.awaitingGeneralConfirmation
        out.generalConfirmationContext = inferred.generalConfirmationContext
      }
    } else if (!out.awaitingGeneralConfirmation) {
      out.awaitingGeneralConfirmation = true
    }
    if (md.patientSwitchConfirmResolved === true || md.patient_switch_confirm_resolved === true) {
      out.patientSwitchConfirmResolved = true
    }
    const readModalityPrompt = parseReadModalityConfirmFromMetadata(md as Record<string, unknown>)
    if (readModalityPrompt) out.readModalityYesNoPrompt = readModalityPrompt
    if (
      md.read_modality_confirmed === true ||
      md.read_modality_cancelled === true ||
      md.readModalityConfirmed === true ||
      md.readModalityCancelled === true ||
      md.read_document_confirmed === true ||
      md.read_document_cancelled === true ||
      md.readDocumentConfirmed === true ||
      md.readDocumentCancelled === true ||
      md.readModalityYesNoResolved === true
    ) {
      out.readModalityYesNoResolved = true
    }
    const checkExamReceiptPrompt = parseCheckExamReceiptConfirmFromMetadata(md as Record<string, unknown>)
    if (checkExamReceiptPrompt) out.checkExamReceiptYesNoPrompt = checkExamReceiptPrompt
    if (
      md.check_exam_receipt_confirmed === true ||
      md.check_exam_receipt_cancelled === true ||
      md.checkExamReceiptConfirmed === true ||
      md.checkExamReceiptCancelled === true ||
      md.checkExamReceiptYesNoResolved === true
    ) {
      out.checkExamReceiptYesNoResolved = true
    }
    if (typeof md.awaitingRemindConfirmation === 'boolean') out.awaitingRemindConfirmation = md.awaitingRemindConfirmation
    else if (md.awaiting_remind_confirmation === true) out.awaitingRemindConfirmation = true
    const remindEditFieldRaw = md.awaitingRemindEditField ?? md.awaiting_remind_edit_field
    if (
      remindEditFieldRaw === 'purpose' ||
      remindEditFieldRaw === 'day' ||
      remindEditFieldRaw === 'time'
    ) {
      out.awaitingRemindEditField = remindEditFieldRaw
    }
    if (md.reminderPreviewUpdateInPlace === true || md.reminder_preview_update_in_place === true) {
      out.reminderPreviewUpdateInPlace = true
    }
    if (md.secretaryPreviewUpdateInPlace === true || md.secretary_preview_update_in_place === true) {
      out.secretaryPreviewUpdateInPlace = true
    }
    if (md.kind === 'calendar_time_retrieval' && md.needs_date_clarification === true) {
      out.calendarTimeNeedsDateClarification = true
    }
    if (typeof md.reminderYesNoSuperseded === 'boolean') out.reminderYesNoSuperseded = md.reminderYesNoSuperseded
    if (typeof md.reminderPreviewResolved === 'boolean') out.reminderPreviewResolved = md.reminderPreviewResolved
    if (typeof md.reminderConfirmed === 'boolean') out.reminderConfirmed = md.reminderConfirmed
    else if (md.reminder_confirmed === true || md.reminder_api_success === true) out.reminderConfirmed = true
    if (typeof md.reminderCancelled === 'boolean') out.reminderCancelled = md.reminderCancelled
    else if (md.remind_confirmation === 'cancelled' || md.reminder_cancelled === true) {
      out.reminderCancelled = true
    }
    if (md.kind === 'remind_me' && !out.reminderConfirmed && !out.reminderCancelled) {
      out.remindFlowActive = true
    }
    const notePreviewRaw =
      (md.notePreview && typeof md.notePreview === 'object' ? md.notePreview : null) ||
      (md.note_preview && typeof md.note_preview === 'object' ? md.note_preview : null)
    if (notePreviewRaw) {
      out.notePreview = wireNotePreviewPayload(notePreviewRaw as Record<string, unknown>)
    }
    if (typeof md.awaitingNoteConfirmation === 'boolean') out.awaitingNoteConfirmation = md.awaitingNoteConfirmation
    else if (md.awaiting_note_confirmation === true) out.awaitingNoteConfirmation = true
    if (typeof md.noteConfirmed === 'boolean') out.noteConfirmed = md.noteConfirmed
    else if (md.note_confirmed === true || md.note_api_success === true) out.noteConfirmed = true
    if (typeof md.noteCancelled === 'boolean') out.noteCancelled = md.noteCancelled
    else if (md.note_confirmation === 'cancelled' || md.note_cancelled === true) out.noteCancelled = true
    if (
      md.awaiting_note_content_clarification === true ||
      md.awaitingNoteContentClarification === true ||
      md.note_clarification === 'missing_content'
    ) {
      out.noteAwaitingContentClarification = true
    }
    if (md.kind === 'create_note_task' && !out.noteConfirmed && !out.noteCancelled) {
      out.noteFlowActive = true
    }
    if (typeof md.openPatientDossierUrl === 'string') out.openPatientDossierUrl = md.openPatientDossierUrl
    const sportsFields = sportsNocFieldsFromAssistantMetadata(md)
    if (sportsFields.sportsNocFrench === true) out.sportsNocFrench = true
    else if (sportsFields.sportsNocFrench === false) out.sportsNocFrench = false
    if (sportsFields.sportsNocAwaitingField) out.sportsNocAwaitingField = sportsFields.sportsNocAwaitingField
    if (sportsFields.sportsNocNeedsClarification) out.sportsNocNeedsClarification = true
    if (sportsFields.sportsNocSummary) out.sportsNocSummary = sportsFields.sportsNocSummary
    if (sportsFields.sportsNocYesNoPrompt) out.sportsNocYesNoPrompt = sportsFields.sportsNocYesNoPrompt
    if (sportsFields.sportsNocCertificateGenerated) out.sportsNocCertificateGenerated = true
    if (sportsFields.sportsNocPreviewUpdateInPlace) out.sportsNocPreviewUpdateInPlace = true
    if (sportsFields.sportsNocLocationOptions?.length) {
      out.sportsNocLocationOptions = sportsFields.sportsNocLocationOptions
      if (!out.uiType && out.sportsNocAwaitingField === 'location') {
        out.uiType = 'sports_noc_location_options'
      }
    }
    if (sportsFields.sportsNocSportOptions?.length) {
      out.sportsNocSportOptions = sportsFields.sportsNocSportOptions
      if (!out.uiType && out.sportsNocAwaitingField === 'sports') {
        out.uiType = 'sports_noc_sports_options'
      }
    }
    if (Array.isArray(sportsFields.sportsNocSportSelectedIds)) {
      out.sportsNocSportSelectedIds = sportsFields.sportsNocSportSelectedIds
    }
    if (
      sportsFields.sportsNocMailPreview &&
      sportsFields.sportsNocYesNoPrompt?.kind === 'mail_certificate' &&
      !out.mailDocument
    ) {
      const mp = sportsFields.sportsNocMailPreview
      out.mailDocument = {
        currentIntent: 'mail_document',
        documentType: mp.documentType,
        mailSubject: mp.mailSubject,
        mailBody: mp.mailBody,
        recipientType: mp.recipientType,
        recipientName: mp.recipientName,
        awaitingMailConfirmation: true,
      }
      out.awaitingMailConfirmation = true
    }
    if (md.sportsNocGenerateConfirm === true) out.sportsNocGenerateConfirm = true
    if (
      out.sportsNocAwaitingField === 'generate_confirm' &&
      out.sportsNocSummary &&
      out.sportsNocYesNoPrompt?.kind === 'generate_certificate'
    ) {
      out.sportsNocGenerateConfirm = true
    }
    if (typeof md.sportsNocYesNoResolved === 'boolean') out.sportsNocYesNoResolved = md.sportsNocYesNoResolved
    if (!asHistorySnapshot) {
      const apiBase = getDefaultVoiceNavApiBaseFromCatalog()
      const coerced = resolveSportsNocCertificatePreviewUrlFromMetadata(md, apiBase)
      if (coerced) {
        try {
          const parsed = new URL(coerced)
          const host = parsed.hostname.toLowerCase()
          const isVoice = isVoiceNavHostname(host, apiBase)
          const isMedical = isMedicalLinkHostname(host)
          const ok =
            (parsed.protocol === 'https:' && (isMedical || isVoice)) ||
            (parsed.protocol === 'http:' && isVoice)
          if (ok) {
            out.sportsNocCertificatePreview = true
            out.modalityMeta = { filePath: coerced, showExamDetails: false }
            out.autoOpenModalityFile = true
          }
        } catch {
          /* ignore invalid URL */
        }
      }
    }
    if (md.renewOrdonnanceMailPrompt && typeof md.renewOrdonnanceMailPrompt === 'object')
      out.renewOrdonnanceMailPrompt = md.renewOrdonnanceMailPrompt as ChatMessage['renewOrdonnanceMailPrompt']
    if (typeof md.renewOrdonnanceMailResolved === 'boolean') out.renewOrdonnanceMailResolved = md.renewOrdonnanceMailResolved
    if (md.renewOrdonnanceRenewalCompleted === true) out.renewOrdonnanceRenewalCompleted = true
    if (md.renewOrdonnanceFrench === true) out.renewOrdonnanceFrench = true
    else if (md.renewOrdonnanceFrench === false) out.renewOrdonnanceFrench = false
    if (md.renewOrdonnanceYesNoPrompt && typeof md.renewOrdonnanceYesNoPrompt === 'object') {
      const k = String((md.renewOrdonnanceYesNoPrompt as { kind?: string }).kind ?? '').trim()
      if (k === 'mail_renewed_ordonnance') out.renewOrdonnanceYesNoPrompt = { kind: 'mail_renewed_ordonnance' }
    }
    if (out.renewOrdonnanceMeta) {
      const bubble = renewOrdonnanceAssistantBubbleText({
        ...out,
        role: 'assistant',
        content: chatContentString(out.content),
        timestamp: out.timestamp ?? 0,
      })
      if (bubble) out.content = bubble
    }
    if (!asHistorySnapshot && out.renewOrdonnanceRenewalCompleted) {
      const renewResolveMd: Record<string, unknown> | null =
        String(md.kind ?? '').trim() === 'renew_ordonnance'
          ? (md as Record<string, unknown>)
          : out.renewOrdonnanceMeta
            ? { kind: 'renew_ordonnance', renew_ordonnance: out.renewOrdonnanceMeta as Record<string, unknown> }
            : null
      if (renewResolveMd) {
        const apiBase = getDefaultVoiceNavApiBaseFromCatalog()
        const renewCoerced = resolveRenewOrdonnancePreviewUrlFromMetadata(renewResolveMd, apiBase)
        if (renewCoerced) {
          try {
            const parsed = new URL(renewCoerced)
            const host = parsed.hostname.toLowerCase()
            const isVoice = isVoiceNavHostname(host, apiBase)
            const isMedical = isMedicalLinkHostname(host)
            const ok =
              (parsed.protocol === 'https:' && (isMedical || isVoice)) ||
              (parsed.protocol === 'http:' && isVoice)
            if (ok) {
              out.renewOrdonnanceDocumentPreview = true
              out.modalityMeta = { filePath: renewCoerced, showExamDetails: false }
              out.autoOpenModalityFile = true
              out.autoOpenPrescriptionDocument = true
              out.prescriptionDocumentPreviewUrl = renewCoerced
              out.prioritizeDocumentAutoOpen = true
            }
          } catch {
            /* ignore invalid URL */
          }
        }
      }
    }
    return asHistorySnapshot ? sanitizeHistorySnapshotMessage(out) : out
  })
}
